// MANISH CUSTOM CODE FOR MODAL OPTIONS - START
// Fragrance modal: View opens a lightbox, Select becomes a quantity stepper
// so the same fragrance can be chosen multiple times up to max_selections.
//
// THIS UPDATE - NEVER RE-ENABLE ADD TO CART ON A SOLD-OUT VARIANT: the
// 150ms enforcement loop below forces Add to Cart enabled as soon as a
// fragrance has been confirmed. It had no idea about stock, so on a
// sold-out variant it would switch a correctly-disabled button back on.
// findAddToCartButtons() now returns nothing while the SELECTED variant is
// out of stock (see isVariantUnavailable()), so every place that enables,
// disables or enforces the button simply leaves it alone in that case and
// the theme's own sold-out state stays in charge. Availability comes from
// the shared data block blocks/buy-buttons.liquid renders in the product
// form (data-mm-variant-data) and the form's own hidden input[name="id"]
// - the same two things the waitlist box uses.
//
// THIS UPDATE (earlier): the footer was redesigned (see mm-modal-options.liquid) to
// show a plain "Selected: X/Y" count instead of the old separate
// "Please select N more" status bar above two full-width buttons.
// updateSummary() now just writes the running total into that count
// span instead of swapping sentence text / toggling a status-bar class,
// and connectedCallback() reads that span (`[data-mm-selected-count]`)
// instead of the removed `.mm-options-modal__summary-text` /
// `.mm-options-modal__summary` elements, which no longer exist in the
// markup. Everything else - max-selection enforcement, the quantity
// stepper, Confirm being disabled until the count is met - is unchanged.
//
// Defense-in-depth guard in changeQty() for options marked disabled
// (status: "coming_soon" / "sold_out" in the metafield). The Liquid
// template already doesn't render a Select button or counter for a
// disabled card, so nothing in the UI can trigger changeQty() for one
// today - this guard just makes that a hard rule at the JS level too, in
// case a future change ever wires up a click path that reaches here.
//
// *** ACTUAL ROOT CAUSE FOUND AND FIXED (earlier update, unchanged) ***
// Confirmed via a real browser console error:
//   Uncaught TypeError: Cannot read properties of null (reading 'querySelector')
//     at MmOptionsModal.connectedCallback (mm-modal-options.js:50:36)
//     at MmOptionsModal.connectedCallback (mm-modal-options.js:66:21)
//
// Moving a custom element to a new parent via `document.body.appendChild(this)`
// (the "portal" fix from an earlier update, which correctly solved the
// modal being trapped inside a transformed ancestor) has a side effect I
// missed: the browser DISCONNECTS and immediately RE-CONNECTS the element
// as part of that move — meaning connectedCallback() fires a SECOND time,
// synchronously, in the middle of the first call. On that second call,
// the element is now inside <body>, no longer inside its original
// `.mm-modal-options` wrapper — so `this.wrapper` is null, and the very
// next line that uses it crashes the entire script. Everything after
// that point (event listeners, disableAddToCart, the enforcement loop)
// never ran, on every single page load, which is why so many things kept
// intermittently seeming to almost-but-not-quite work — the exact set of
// things that "worked" depended on what had executed before the crash.
//
// FIX: a simple guard flag. If connectedCallback fires again on an
// instance that has already completed setup, it returns immediately
// instead of re-running (and crashing on) the full initialization.

class MmOptionsModal extends HTMLElement {
  connectedCallback() {
    // Guard against the re-entrant connectedCallback call caused by the
    // appendChild-based portal below. `this` is the SAME object instance
    // across connect/disconnect/reconnect, so this flag correctly persists.
    if (this._mmInitialized) return;

    this.wrapper = this.closest('.mm-modal-options');
    this.productFormButtons = this.wrapper?.closest('form')?.querySelector('.product-form-buttons')
      || this.wrapper?.closest('product-form-component')?.querySelector('.product-form-buttons')
      || null;

    if (this.closest('quick-add-dialog')) {
      this._mmInitialized = true;
      this.setupQuickViewFallback();
      return;
    }

    this.maxSelections = parseInt(this.dataset.maxSelections || '2', 10);
    this.qtyByOption = new Map();
    this.confirmed = false;

    this.backdrop = this.querySelector('[data-mm-backdrop]');
    this.dialog = this.querySelector('.mm-options-modal__dialog');
    this.openButton = this.wrapper.querySelector('.mm-modal-options__button');
    this.closeButton = this.querySelector('.mm-options-modal__close');
    this.cancelButton = this.querySelector('.mm-options-modal__cancel');
    this.confirmButton = this.querySelector('.mm-options-modal__confirm');
    this.selectedCountEl = this.querySelector('[data-mm-selected-count]');
    this.status = this.wrapper.querySelector('.mm-modal-options__status');
    this.statusText = this.status ? this.status.querySelector('.mm-modal-options__status-text') : null;
    this.propertiesContainer = this.wrapper.querySelector('[data-mm-properties]');
    this.grid = this.querySelector('.mm-options-modal__grid');
    this.lightbox = this.querySelector('.mm-lightbox');
    this.lightboxImage = this.querySelector('.mm-lightbox__image');
    this.lightboxName = this.querySelector('.mm-lightbox__name');
    this.lightboxDescription = this.querySelector('.mm-lightbox__description');

    // Mark initialized BEFORE the portal move, so the re-entrant call
    // triggered by appendChild hits the guard at the top and returns
    // immediately instead of crashing.
    this._mmInitialized = true;

    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.openButton.addEventListener('click', () => this.openModal());
    this.closeButton.addEventListener('click', () => this.closeModal());
    this.cancelButton.addEventListener('click', () => this.closeModal());
    this.confirmButton.addEventListener('click', () => this.confirmSelection());
    this.backdrop.addEventListener('click', () => this.closeModal());

    this.grid.addEventListener('click', (e) => this.handleGridClick(e));
    this.lightbox.addEventListener('click', (e) => {
      if (e.target.closest('[data-lightbox-close]')) this.closeLightbox();
    });

    this.onKeydown = (e) => {
      if (e.key !== 'Escape') return;
      if (!this.dialog.hidden) {
        e.stopPropagation();
        this.closeModal();
      } else if (this.lightbox && !this.lightbox.hidden) {
        e.stopPropagation();
        this.closeLightbox();
      }
    };
    document.addEventListener('keydown', this.onKeydown, true);

    this.disableAddToCart();
    this.setupStickyBarPopupHint();
    this.setupPersistentEnforcement();
  }

  disconnectedCallback() {
    if (this.onKeydown) {
      document.removeEventListener('keydown', this.onKeydown, true);
    }
    if (this.enforcementInterval) {
      clearInterval(this.enforcementInterval);
    }
  }

  setupQuickViewFallback() {
    if (this.wrapper) {
      this.wrapper.style.display = 'none';
    }
    if (this.productFormButtons && !this.productFormButtons.dataset.mmViewProductAdded) {
      this.productFormButtons.dataset.mmViewProductAdded = 'true';
      this.productFormButtons.style.display = 'none';

      const productUrl = this.wrapper?.dataset.productUrl;
      if (productUrl) {
        const link = document.createElement('a');
        link.href = productUrl;
        link.className = 'button mm-view-product-link';
        link.textContent = 'View Product';
        this.productFormButtons.insertAdjacentElement('afterend', link);
      }
    }
  }

  findForm() {
    return this.wrapper?.closest('form') || this.wrapper?.closest('product-form-component')?.querySelector('form');
  }

  // True when the variant currently selected in this product's form is out
  // of stock. Reads the shared availability data block rendered by
  // blocks/buy-buttons.liquid and the form's own hidden variant id input.
  // Anything unexpected (no data block, unknown id, bad JSON) counts as
  // "not unavailable" so the picker's original behaviour is the fallback.
  isVariantUnavailable() {
    const form = this.findForm();
    if (!form) return false;
    const dataEl = form.querySelector('[data-mm-variant-data]');
    const idInput = form.querySelector('input[name="id"]');
    if (!dataEl || !idInput) return false;
    try {
      const entry = JSON.parse(dataEl.textContent)[idInput.value];
      return !!entry && entry.a === false;
    } catch (e) {
      return false;
    }
  }

  findAddToCartButtons() {
    const form = this.findForm();
    if (!form) return [];
    // Sold-out variant: leave Add to Cart entirely to the theme.
    if (this.isVariantUnavailable()) return [];
    return Array.from(
      form.querySelectorAll('[name="add"], .add-to-cart-button, .product-form__submit, [ref="addToCartButton"]')
    );
  }

  disableAddToCart() {
    this.findAddToCartButtons().forEach((btn) => {
      btn.disabled = true;
      btn.classList.add('mm-requires-selection');
    });
    if (this.productFormButtons) {
      this.productFormButtons.classList.add('mm-selection-required');
    }
  }

  enableAddToCart() {
    this.findAddToCartButtons().forEach((btn) => {
      btn.disabled = false;
      btn.classList.remove('mm-requires-selection');
    });
    if (this.productFormButtons) {
      this.productFormButtons.classList.remove('mm-selection-required');
    }
  }

  setupPersistentEnforcement() {
    this.enforcementInterval = setInterval(() => {
      const buttons = this.findAddToCartButtons();
      buttons.forEach((btn) => {
        if (this.confirmed && btn.disabled) {
          btn.disabled = false;
          btn.classList.remove('mm-requires-selection');
        } else if (!this.confirmed && !btn.disabled) {
          btn.disabled = true;
          btn.classList.add('mm-requires-selection');
        }
      });
    }, 150);
  }

  setupStickyBarPopupHint() {
    const stickyBar = document.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
    if (!stickyBar || stickyBar.dataset.mmStickyHintBound) return;
    stickyBar.dataset.mmStickyHintBound = 'true';

    stickyBar.addEventListener('click', () => {
      if (!this.confirmed) {
        this.openModal();
      }
    });
  }

  openModal() {
    this.backdrop.hidden = false;
    this.dialog.hidden = false;
    this.dialog.focus();
    this.openButton.setAttribute('aria-expanded', 'true');
  }

  closeModal() {
    this.backdrop.hidden = true;
    this.dialog.hidden = true;
    this.openButton.setAttribute('aria-expanded', 'false');
    this.openButton.focus();
  }

  openLightbox(btn) {
    this.lightboxImage.src = btn.dataset.optionImage || '';
    this.lightboxImage.alt = btn.dataset.optionName || '';
    this.lightboxName.textContent = btn.dataset.optionName || '';
    this.lightboxDescription.textContent = btn.dataset.optionDescription || '';
    this.lightbox.hidden = false;
  }

  closeLightbox() {
    this.lightbox.hidden = true;
  }

  getTotalSelected() {
    let total = 0;
    this.qtyByOption.forEach((entry) => (total += entry.qty));
    return total;
  }

  handleGridClick(e) {
    const viewBtn = e.target.closest('.mm-option-card__view');
    if (viewBtn) {
      this.openLightbox(viewBtn);
      return;
    }

    const selectBtn = e.target.closest('.mm-option-card__select');
    if (selectBtn) {
      this.changeQty(selectBtn.dataset.optionId, selectBtn.dataset.optionName, 1);
      return;
    }

    const stepBtn = e.target.closest('.mm-counter__btn');
    if (stepBtn) {
      const card = stepBtn.closest('.mm-option-card');
      const delta = stepBtn.dataset.action === 'increase' ? 1 : -1;
      this.changeQty(card.dataset.optionId, stepBtn.dataset.optionName, delta);
    }
  }

  changeQty(id, name, delta) {
    const card = this.grid.querySelector(`.mm-option-card[data-option-id="${id}"]`);
    if (!card) return;

    // A "Coming Soon" / "Sold Out" option never has a Select button or
    // counter rendered in the markup, so nothing in the UI reaches this
    // point for one today - this check just makes that a hard rule at
    // the JS level too, rather than relying only on absent markup.
    if (card.dataset.disabled === 'true') return;

    const entry = this.qtyByOption.get(id) || { name, qty: 0 };
    const total = this.getTotalSelected();

    if (delta > 0 && total >= this.maxSelections) {
      return;
    }

    entry.qty = Math.max(0, entry.qty + delta);

    if (entry.qty === 0) {
      this.qtyByOption.delete(id);
    } else {
      this.qtyByOption.set(id, entry);
    }

    card.dataset.qty = entry.qty || 0;
    this.renderCardActions(card, id, name, entry.qty || 0);
    this.confirmed = false;
    this.updateSummary();
  }

  renderCardActions(card, id, name, qty) {
    const actions = card.querySelector('.mm-option-card__actions');
    const viewBtn = card.querySelector('.mm-option-card__view');
    const viewHtml = viewBtn.outerHTML;

    if (qty === 0) {
      actions.innerHTML = `${viewHtml}<button type="button" class="mm-option-card__select" data-option-id="${id}" data-option-name="${name}">Select</button>`;
    } else {
      const total = this.getTotalSelected();
      const atCapacity = total >= this.maxSelections;
      actions.innerHTML = `${viewHtml}<div class="mm-option-card__counter">
        <button type="button" class="mm-counter__btn" data-action="decrease" data-option-name="${name}" aria-label="Decrease ${name}">−</button>
        <span class="mm-counter__value">${qty}</span>
        <button type="button" class="mm-counter__btn" data-action="increase" data-option-name="${name}" ${atCapacity ? 'disabled' : ''} aria-label="Increase ${name}">+</button>
      </div>`;
    }
  }

  updateSummary() {
    const total = this.getTotalSelected();

    if (this.selectedCountEl) {
      this.selectedCountEl.textContent = total;
    }
    this.confirmButton.disabled = total !== this.maxSelections;

    const atCapacity = total >= this.maxSelections;
    // Only touches enabled cards' Select buttons - disabled cards never
    // have a `.mm-option-card__select` element to match in the first place.
    this.grid.querySelectorAll('.mm-option-card__select').forEach((btn) => {
      btn.disabled = atCapacity;
      btn.style.opacity = atCapacity ? '0.5' : '';
      btn.style.cursor = atCapacity ? 'not-allowed' : '';
    });
  }

  buildSelectedUnits() {
    const units = [];
    this.qtyByOption.forEach((entry) => {
      for (let i = 0; i < entry.qty; i++) {
        units.push(entry.name);
      }
    });
    return units;
  }

  confirmSelection() {
    const total = this.getTotalSelected();
    if (total !== this.maxSelections) return;

    const units = this.buildSelectedUnits();

    this.propertiesContainer.innerHTML = '';
    units.forEach((name, index) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[Fragrance ${index + 1}]`;
      input.value = name;
      this.propertiesContainer.appendChild(input);
    });

    const validationInput = document.createElement('input');
    validationInput.type = 'hidden';
    validationInput.name = 'properties[_mm_fragrance_selected]';
    validationInput.value = 'true';
    this.propertiesContainer.appendChild(validationInput);

    if (this.statusText) {
      const displayText = units
        .map((name, i) => `Fragrance ${i + 1}: ${name}`)
        .join(', ');
      this.statusText.textContent = displayText;
    }
    if (this.status) {
      this.status.dataset.status = 'selected';
    }

    this.confirmed = true;
    this.enableAddToCart();
    this.closeModal();
  }
}

customElements.define('mm-options-modal', MmOptionsModal);

// MANISH CUSTOM CODE FOR MODAL OPTIONS - END
